/* Cajita — UI primitives (portados del prototipo claude.ai/design) */
import type { ReactNode } from 'react';
import { Sparkline } from './charts';

const statusMap: Record<string, string> = {
  Aceptado: 'green',
  Pagado: 'green',
  Saldada: 'green',
  OK: 'green',
  Activo: 'green',
  Pagada: 'green',
  Pendiente: 'amber',
  'Por vencer': 'amber',
  Observado: 'amber',
  Abierta: 'blue',
  Parcial: 'blue',
  Rechazado: 'red',
  Vencido: 'red',
  Anulado: 'red',
  Faltante: 'red',
  Inactivo: 'gray',
  Contado: 'lime',
  Crédito: 'blue',
  Borrador: 'gray',
};

export function Status({ children, tone }: { children: string; tone?: string }): JSX.Element {
  const t = tone || statusMap[children] || 'gray';
  return (
    <span className={`badge badge-${t}`}>
      <span className="bdot" />
      {children}
    </span>
  );
}

const tintMap: Record<string, string> = {
  '#3B82F6': 'var(--tint-blue)',
  '#60A5FA': 'var(--tint-blue)',
  '#84CC16': 'var(--tint-lime)',
  '#22C55E': 'var(--tint-lime)',
  '#34D399': 'var(--tint-lime)',
  '#EF4444': 'var(--tint-peach)',
  '#F87171': 'var(--tint-peach)',
  '#F59E0B': 'var(--tint-yellow)',
  '#FBBF24': 'var(--tint-yellow)',
  '#8B5CF6': 'var(--tint-violet)',
  '#0EA5E9': 'var(--tint-sky)',
};

export function StatCard({
  label,
  value,
  icon,
  delta,
  deltaDir,
  spark,
  sparkColor,
  accent,
  sub,
  onClick,
  variant = 'default',
}: {
  label: string;
  value: string;
  icon?: ReactNode;
  delta?: string;
  deltaDir?: 'up' | 'down';
  spark?: number[];
  sparkColor?: string;
  accent?: string;
  sub?: string;
  onClick?: () => void;
  variant?: 'default' | 'filled';
}): JSX.Element {
  const up = deltaDir === 'up';
  if (variant === 'filled') {
    return (
      <div
        className="card card-pad fade-in"
        onClick={onClick}
        style={{
          background: 'linear-gradient(150deg, #D6F6FF 0%, #ABEFFF 58%, #8AE4FB 100%)',
          borderColor: 'transparent',
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 14px 36px -14px rgba(120,210,240,.55)',
          borderStyle: 'solid',
        }}
      >
        <div className="row between aic" style={{ marginBottom: 14 }}>
          <div className="row aic gap8" style={{ color: '#0B5566', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
            {icon}
            {label}
          </div>
          {delta && (
            <span className="badge" style={{ background: 'rgba(17,24,39,.12)', color: '#111827' }}>
              {up ? '▲' : '▼'} {delta}
            </span>
          )}
        </div>
        <div className="t-display tnum" style={{ color: '#0B3A47' }}>
          {value}
        </div>
        {sub && <div style={{ color: '#0B5566', fontSize: 13, marginTop: 4, fontWeight: 500 }}>{sub}</div>}
      </div>
    );
  }
  const tint = accent ? tintMap[accent] || '#fff' : '#fff';
  return (
    <div
      className="card card-pad fade-in"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        background: accent ? `linear-gradient(165deg, ${tint} 0%, #ffffff 64%)` : '#fff',
      }}
    >
      <div className="row between ait">
        <div className="row aic gap10">
          {icon && (
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                display: 'grid',
                placeItems: 'center',
                background: accent ? '#ffffff' : 'var(--canvas)',
                color: accent || 'var(--muted)',
                boxShadow: accent ? '0 1px 3px rgba(17,24,39,.08)' : 'none',
              }}
            >
              {icon}
            </div>
          )}
          <div className="muted fw6" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {label}
          </div>
        </div>
        {delta && <span className={`badge badge-${up ? 'lime' : 'red'}`}>{up ? '▲' : '▼'} {delta}</span>}
      </div>
      <div className="row between aie" style={{ marginTop: 14 }}>
        <div>
          <div className="t-display tnum">{value}</div>
          {sub && (
            <div className="muted t-sm" style={{ marginTop: 3 }}>
              {sub}
            </div>
          )}
        </div>
        {spark && <Sparkline data={spark} color={sparkColor || '#84CC16'} />}
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="page-head">
      <div>
        <h1 className="t-display">{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="row gap10 wrap">{actions}</div>}
    </div>
  );
}

export function ProdThumb({
  p,
  size = 40,
  radius = 10,
}: {
  p: { img: string; emoji: string };
  size?: number;
  radius?: number;
}): JSX.Element {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: p.img,
        display: 'grid',
        placeItems: 'center',
        fontSize: size * 0.5,
        flex: 'none',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
      }}
    >
      {p.emoji}
    </div>
  );
}
